import { FC, useState } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";
import { PlotEvent } from "@ant-design/charts";
import * as _ from "lodash";

import {
  ChartFilters,
  Charts,
  ExtendedEventEntity,
  Filter,
  Group,
  MetricEntity,
  filterToSeriesMap,
  SavedChart,
  V3DisplayFilter
} from "@ctra/api";

import {
  Charts as ChartUI,
  CtraLayout,
  SwitchWithLabel,
  Row,
  Col,
  Typography,
  Button,
  PlusOutlined,
  CloseOutlined,
  StarOutlined,
  Modal,
  Space,
  AntDesignSelect as Select,
  StarFilled,
  StarHalfFilled,
  Icon
} from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Nullable, parseQuery } from "@ctra/utils";

import { useFarm } from "@farms";
import { URIState } from "@routes";
import {
  Chart,
  ChartAPIContext,
  ChartFilterContext,
  EventList,
  ISODurationFilter,
  useTimePeriod
} from "@chart";
import { EventListContext } from "@events";
import { useDataDictionary } from "@base";
import { useSavedCharts, alignLegends, GAActions, formatFilters } from "@analytics";

import styles from "./ConfigPage.module.less";
import { MetricPicker } from "../MetricPicker";
import { ChartSelector } from "../ChartSelector";
import { CorrelationList } from "../CorrelationList";

import { commonModalProps } from "../DashboardPage";
import { useGoogleAnalytics } from "@ctra/analytics";

const { WidgetWrapper, ContentWrapper } = CtraLayout;
const { ChartVariant } = ChartUI;
const { Paragraph, Text } = Typography;
const { confirm } = Modal;

const {
  analytics: {
    correlations: { pageDescription, events },
    v3: {
      confirm: { saveChart: save },
      config: { cta }
    }
  },
  timeline: {
    createEvent: { submit }
  }
} = Content;

/**
 * Chart configurator page
 * @return {JSX.Element}
 * @constructor
 */
export const ConfigPage: FC = () => {
  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();
  const { search, state } = useLocation<URIState>();
  const { farm: farmContext } = useFarm();
  const { params } = useRouteMatch<{ metricID: MetricEntity["id"] }>();

  const [showEvents, setShowEvents] = useState<boolean>(false);
  const [eventList, setEventList] = useState<ExtendedEventEntity[]>([]);
  const [newMetric, setNewMetric] = useState<MetricEntity["id"]>();
  const [savingVariant, setSavingVariant] = useState<MetricEntity["id"]>();

  const {
    metrics,
    dataDescriptors,
    meta: { isLoading },
    groups: { families },
    api: { extractSource }
  } = useDataDictionary();

  const {
    savedCharts,
    api: { saveChart, removeChart, saveChartChanges },
    meta: { isUpdating }
  } = useSavedCharts();

  const savedChart = state?.savedChart;
  const searchParams = new URLSearchParams(search);
  const query = parseQuery(searchParams, "source", "variant");
  const initialMetricID = params.metricID;
  const initialSourceID = _.defaultTo(query.source, void 0);
  const initialVariantID = _.defaultTo(query.variant, void 0);

  /**
   * Handle a metric value change
   * @param {MetricEntity["id"]} metric
   * @param {string} source
   * @param {string} variant
   * @returns {{metric: string, variant: string, source: any}}
   */
  const handleMetricChanges = (metric: MetricEntity["id"], source?: string, variant?: string) => {
    const initialSources = _.get(families, metric);
    const initialSourceID = source || _.first(initialSources)?.id;

    const initialVariants = _.get(metrics, [initialSourceID || metric, "variants"]);
    /**
     * Get the farm type variant
     */
    const farmVariant = _.find(initialVariants, (variant) =>
      _.isEqual(_.get(dataDescriptors, [variant, "dataProperties", "type"]), "farm")
    );
    /**
     * If no farm selected, then use the per farm variant, otherwise use the variant passed. Default to the first one
     */
    const initialVariantID = (
      !farmContext ? farmVariant : _.defaultTo(variant, farmVariant || _.first(initialVariants))
    ) as string;

    return { metric, source: initialSourceID, variant: initialVariantID };
  };

  /**
   * Handle adding new metric to the list
   */
  const addNewMetric = () => {
    if (newMetric) {
      trackEvent(GAActions.addMetricToList);
      setChartList((list) => [...list, handleMetricChanges(newMetric)]);
      setNewMetric(void 0);
    }
  };

  /**
   * Handle saving a chart to a dashboard tab
   *
   * @param {MetricEntity["id"]} variantID
   * @param {string} view
   * @param {ChartFilters} dataFilters
   * @param {SavedChart["displayFilters"]} displayFilters
   */
  const handleSave = (
    variantID: MetricEntity["id"],
    view: string,
    dataFilters: ChartFilters,
    displayFilters?: SavedChart["displayFilters"]
  ) => {
    setSavingVariant(variantID);
    /**
     * Creating variable here because useState isn't updated for the context of this function
     */
    let group = state?.dashboard || _.first(savedCharts)?.groupName;

    confirm({
      ...commonModalProps,
      okText: t(submit),
      title: (
        <Space>
          <StarOutlined />
          {t(save.title)}
        </Space>
      ),
      content: (
        <Space direction="vertical">
          <Text>{t(save.description)}</Text>
          <Select
            size="large"
            style={{ width: "100%" }}
            defaultValue={group}
            onChange={(value) => {
              group = value;
            }}
          >
            {_.map(savedCharts, ({ groupName }) => (
              <Select.Option key={groupName} value={groupName}>
                {groupName}
              </Select.Option>
            ))}
          </Select>
        </Space>
      ),
      onOk() {
        const dateRange = isoDuration ? [isoDuration] : [timePeriod.startDate, timePeriod.endDate];
        /**
         * convert filters into type + value format
         */
        const convertedDataFilters = _.map(
          dataFilters,
          (value, key) =>
            ({
              type: _.get(filterToSeriesMap, key),
              values: value
            } as Filter)
        );

        trackEvent(GAActions.saveMetric);

        group &&
          saveChart(variantID, group, {
            chartType: view,
            dateRange,
            dataFilters: convertedDataFilters,
            displayFilters
          });
      }
    });
  };

  /**
   * Get the groupName of the saved variant
   * @param variantID
   * @returns
   */
  const getGroupName = (variantID: MetricEntity["id"]) => {
    const group = _.find(savedCharts, (group) => _.find(group.charts, { variantId: variantID })) as Group;

    return group?.groupName;
  };

  /**
   * Get the saved chart based on the variant
   * @param variantID
   * @returns
   */
  const getSavedChart = (variantID: MetricEntity["id"]): SavedChart => {
    const allCharts = _.flatMap(savedCharts, "charts");
    return _.find(allCharts, { variantId: variantID });
  };

  /**
   * Remove variant from the list
   * @param variantID
   */
  const handleRemove = (variantID: MetricEntity["id"]) => {
    trackEvent(GAActions.removeMetric);

    removeChart(variantID, getGroupName(variantID));
  };

  /**
   * Check if variant is already saved
   * @param {MetricEntity["id"]} variantID
   * @param {SavedChart["displayFilters"]} filters
   * @returns {boolean}
   */
  const isSaved = (variantID: MetricEntity["id"]) => {
    return _.some(savedCharts, ({ charts }) => _.some(charts, _.matches({ variantId: variantID })));
  };

  /**
   * Check if variant has unsaved changes in filters
   * @param {MetricEntity["id"]} variantID
   * @param {SavedChart["displayFilters"]} filters
   * @returns {boolean}
   */
  const unsavedChanges = (variantID: MetricEntity["id"], updatedChart: SavedChart) => {
    const savedChart = getSavedChart(variantID);

    /**
     * Comparing chart type and display filters for unsaved changes
     * @todo add dataFilters
     */
    const comparisonValues = ["chartType", "displayFilters"];

    /**
     * get rid of null values if any
     */
    const chart = _.mapValues(_.pick(savedChart, comparisonValues), (value) =>
      _.isNull(value) ? void 0 : value
    );

    if (savedChart) {
      return !_.isEqual(_.pick(chart, comparisonValues), _.pick(updatedChart, comparisonValues));
    }
  };

  /**
   * Handle adding new metric from suggested correlations to the list
   * @param {MetricEntity["id"]} variantID
   */
  const addCorrelation = (variantID: MetricEntity["id"]) => {
    const source = extractSource(variantID);
    source && setChartList((list) => [...list, source]);
  };

  /**
   * State to keep the list of metrics
   */
  const [chartList, setChartList] = useState([
    handleMetricChanges(initialMetricID, initialSourceID, initialVariantID)
  ]);

  const initialISODuration = savedChart ? (savedChart.timePeriod ? null : savedChart.isoDuration) : "P3M";
  const [isoDuration, setISOduration] = useState<Nullable<string>>(initialISODuration);
  const [initialTimePeriod] = useTimePeriod({ isoDuration }, _.defaultTo(0, 86400));
  const [timePeriod, setTimePeriod] = useState(savedChart?.timePeriod || initialTimePeriod);

  const eventPanelHidden = _.isEmpty(eventList) || _.isEmpty(chartList);

  const correlationsComponent = (
    <CorrelationList
      variantList={_.map(chartList, "variant")}
      variantID={_.last(chartList)?.variant as string}
      onAdd={addCorrelation}
    />
  );

  return (
    <EventListContext.Provider farmID={farmContext?.id}>
      <WidgetWrapper loading={isLoading} title={<Paragraph>{t<string>(pageDescription)}</Paragraph>}>
        <Row gutter={[24, 0]} wrap={false}>
          <Col flex={1}>
            <ContentWrapper padded={false} className={styles.HideOverflow}>
              <Row gutter={[16, 0]} className={styles.Options} justify="end" align="middle">
                {farmContext && (
                  <Col>
                    <SwitchWithLabel
                      defaultChecked={showEvents}
                      onChange={(checked) => setShowEvents(checked)}
                      disabled={_.isEmpty(chartList)}
                      size="small"
                      label={t(events)}
                    />
                  </Col>
                )}
                <Col>
                  <ISODurationFilter
                    durations={Charts.utils.supportedDurations}
                    duration={isoDuration}
                    timePeriod={timePeriod}
                    onDurationChange={setISOduration}
                    onTimePeriodChange={setTimePeriod}
                  />
                </Col>
              </Row>
              {_.map(chartList, ({ metric, source, variant }, idx, collection) => {
                /**
                 * Value change handler for dropdowns associated with metric/source/variant changes
                 */
                const handleChange = (_metric: string, source?: string, variant?: string) =>
                  setChartList((list) =>
                    _.map(list, (chart) =>
                      _.isEqual(chart.metric, metric) ? handleMetricChanges(_metric, source, variant) : chart
                    )
                  );

                const savedChart = getSavedChart(variant);
                const seriesFilter = _.find(savedChart?.displayFilters, { type: "series" });
                const seriesValues = _.get(seriesFilter, "values", []);
                const dataFilters = formatFilters(savedChart?.dataFilters || []);

                return (
                  <ChartFilterContext.Provider
                    isoDuration={isoDuration}
                    timePeriod={timePeriod}
                    currentValues={{ isoDuration, timePeriod }}
                    series={seriesValues}
                    filters={metric === initialMetricID ? dataFilters : void 0}
                  >
                    <ChartFilterContext.Consumer>
                      {({ series, filters }) =>
                        variant && (
                          <ChartAPIContext.Provider
                            dataDescriptorID={variant}
                            farmID={farmContext?.id}
                            defaultView={savedChart?.chartType}
                          >
                            <ChartAPIContext.Consumer>
                              {({ chart: { view } }) => {
                                /**
                                 * current display filters
                                 */

                                const displayFilters = series.active.length
                                  ? ([
                                      {
                                        type: "series",
                                        values: series.active
                                      }
                                    ] as V3DisplayFilter[])
                                  : void 0;

                                const dateRange = isoDuration
                                  ? [isoDuration]
                                  : [timePeriod.startDate, timePeriod.endDate];

                                /**
                                 * chart that is currently in view
                                 */
                                const currentChart = {
                                  variantId: variant,
                                  displayFilters,
                                  chartType: view,
                                  dateRange,
                                  /**
                                   * @todo display filters
                                   */
                                  dataFilters: []
                                };

                                const currentGroup = getGroupName(variant);

                                return (
                                  <>
                                    <Row wrap={false} align="middle">
                                      <Col flex={1}>
                                        <ChartSelector
                                          metric={metric}
                                          source={source}
                                          variant={variant}
                                          handleChange={handleChange}
                                        />
                                      </Col>
                                      <Col>
                                        <CloseOutlined
                                          className={styles.Close}
                                          onClick={() =>
                                            setChartList((currentChartList) =>
                                              _.filter(
                                                currentChartList,
                                                (item) => !_.isEqual(item.metric, metric)
                                              )
                                            )
                                          }
                                        />
                                      </Col>
                                      <Col>
                                        {isSaved(variant) ? (
                                          unsavedChanges(variant, currentChart) ? (
                                            <Button
                                              loading={isUpdating && _.isEqual(variant, savingVariant)}
                                              className={styles.Save}
                                              icon={<Icon component={StarHalfFilled} />}
                                              size="large"
                                              type="primary"
                                              onClick={() => {
                                                trackEvent(GAActions.saveChanges);

                                                setSavingVariant(variant);
                                                currentGroup &&
                                                  saveChartChanges(variant, currentGroup, currentChart);
                                              }}
                                            >
                                              {t(cta.update)}
                                            </Button>
                                          ) : (
                                            <Button
                                              className={styles.Saved}
                                              icon={<StarFilled />}
                                              size="large"
                                              type="secondary"
                                              onClick={() => handleRemove(variant)}
                                            >
                                              {t(cta.save, { isSaved: true })}
                                            </Button>
                                          )
                                        ) : (
                                          <Button
                                            loading={isUpdating && _.isEqual(variant, savingVariant)}
                                            className={styles.Save}
                                            icon={<StarOutlined />}
                                            size="large"
                                            type="primary"
                                            onClick={() => handleSave(variant, view, filters, displayFilters)}
                                          >
                                            {t(cta.save, { isSaved: false })}
                                          </Button>
                                        )}
                                      </Col>
                                    </Row>
                                    <Row wrap={false} className={styles.Border}>
                                      <Col span={24} className={styles.Chart}>
                                        <Chart
                                          key={chartList.length}
                                          variant={ChartVariant.V3}
                                          viewOptions={{
                                            showEvents,
                                            zoomEnabled: false,
                                            showSupportedViews: true,
                                            showToolbar: false,
                                            showAnnotations: true
                                          }}
                                          config={{
                                            onReady: (plot) => {
                                              plot.on(
                                                "afterrender",
                                                _.throttle(() => {
                                                  if (collection.length > 1) {
                                                    alignLegends();
                                                  }
                                                }, 100)
                                              );

                                              plot.on("element:click", (event: PlotEvent) => {
                                                const shape = _.get(event, ["data", "shape"]);
                                                const eventList = _.get(event, ["data", "data", "events"]);

                                                trackEvent(GAActions.openEvent);

                                                if (shape === "farm-event-point") {
                                                  setEventList(() => eventList);
                                                }
                                              });
                                            },
                                            ...(chartList.length === 1 ? { height: 450 } : {})
                                          }}
                                        />
                                      </Col>
                                    </Row>
                                  </>
                                );
                              }}
                            </ChartAPIContext.Consumer>
                          </ChartAPIContext.Provider>
                        )
                      }
                    </ChartFilterContext.Consumer>
                  </ChartFilterContext.Provider>
                );
              })}
              <Col>{correlationsComponent}</Col>
              <Row gutter={[12, 0]} className={styles.Selector}>
                <Col flex={1}>
                  <MetricPicker
                    selection={_.map(chartList, "metric")}
                    value={newMetric}
                    onChange={setNewMetric}
                  />
                </Col>
                <Col>
                  <Button
                    size="large"
                    disabled={!newMetric}
                    type="secondary"
                    onClick={addNewMetric}
                    icon={<PlusOutlined />}
                  >
                    {t(cta.add)}
                  </Button>
                </Col>
              </Row>
            </ContentWrapper>
          </Col>
          {eventPanelHidden ? null : (
            <Col span={5}>
              <EventList data={eventList} handleClose={() => setEventList([])} />
            </Col>
          )}
        </Row>
      </WidgetWrapper>
    </EventListContext.Provider>
  );
};
